import React, { useMemo, useState, useEffect } from 'react'
import { makeStyles, Typography, Container, MenuItem } from '@material-ui/core'
import TrainingClassDialog from '../../../components/Dialogs/Training/TrainingClassDialog'
import { DIALOG_EDIT_MODES } from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import useQuery from '../../../hooks/useQuery'
import Loading from '../../common/Loading'
import TrainingClassDetailsCard from '../../../components/Training/TrainingClassDetailsCard'
import TrainingClassSessionListCard from '../../../components/Training/TrainingClassSessionListCard'
import StyledChip from '../../../components/Common/StyledChip'
import { standardizeStartOfDateUTC, getClassStatus } from '../../../utils'
import StyledDropdownMenu from '../../../components/Common/Menus/StyledDropdownMenu'
import DangerConfirmationDialog from '../../../components/Common/Dialogs/DangerConfirmation'

const useStyles = makeStyles(() => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px'
	},
	topContainer: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
	headerContainer: { display: 'flex', alignItems: 'center', gap: '1rem' },
	header: { fontWeight: 800, fontSize: '1.375rem' },
	statusChip: {
		marginLeft: 16,
		'& .MuiChip-label': {
			marginRight: 0
		}
	}
}))

const TrainingClass = () => {
	const classes = useStyles()
	const { getQueryParam, params } = useQuery()
	const date = getQueryParam('date') || null
	const [classDetails, setClassDetails] = useState(null)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [currentEditedObject, setCurrentEditedObject] = useState({})
	const [confirmCancelClassDialogOpen, setConfirmCancelClassDialogOpen] = useState(false)
	const [confirmRemoveAttendeeDialogOpen, setConfirmRemoveAttendeeDialogOpen] = useState(false)
	const [attendeeToRemove, setAttendeeToRemove] = useState(null)

	const {
		facility,
		loading,
		trainingCourses,
		trainingLocations,
		trainingClasses,
		getTrainingClassForFacility,
		getTrainingCoursesForFacility,
		getTrainingLocationsForFacility,
		cancelTrainingClassForFacility,
		cancelEnrollmentForClass
	} = useSelectedFacility()

	const handleEditDialogOpen = () => {
		setEditDialogOpen(true)
	}

	const handleEditDialogClose = () => {
		setEditDialogOpen(false)
	}

	const handleEditClassButtonClick = () => {
		if (Object.entries(currentEditedObject).length > 0) {
			handleEditDialogOpen()
		}
	}

	const handleCancelClassButtonClick = async () => {
		setConfirmCancelClassDialogOpen(true)
	}

	const handleCancelClass = async () => {
		setConfirmCancelClassDialogOpen(false)
		if (classDetails) {
			await cancelTrainingClassForFacility({
				mongo_class_id: classDetails._id,
				facility_id: facility._id
			})
		}
	}

	const handleRemoveAttendeeRequest = (attendee) => {
		const { pet = [], handlers = [] } = attendee || {}
		const remove = {
			primary_handler: handlers.find((h) => h.primary),
			handler_names: handlers.map((h) => h.full_name),
			handler_ids: handlers.map((h) => h._id),
			pet_name: pet[0] ? pet[0].name : null,
			pet_id: pet[0] ? pet[0]._id : null
		}

		setAttendeeToRemove(remove)
		setConfirmRemoveAttendeeDialogOpen(true)
	}

	const handleCancelRemoveAttendeeRequest = () => {
		setConfirmRemoveAttendeeDialogOpen(false)
		setTimeout(() => {
			setAttendeeToRemove(null)
		}, 500)
	}

	const handleRemoveAttendee = async () => {
		const { pet_id, handler_ids } = attendeeToRemove || {}
		const { _id } = classDetails
		if (!handler_ids || !pet_id) {
			return
		}

		await cancelEnrollmentForClass({
			mongo_class_id: _id,
			facility_id: facility._id,
			handler_ids,
			pet_id
		})

		handleCancelRemoveAttendeeRequest()
	}

	useEffect(() => {
		const classId = params.classId
		if (!trainingClasses || trainingClasses.length === 0 || !classId) {
			return
		}

		const foundClass = trainingClasses[classId] || null
		setClassDetails(foundClass)

		if (foundClass) {
			const attendeesMap = new Map()

			foundClass.attendance_by_session.forEach((session) => {
				const { attendance = [] } = session
				attendance.forEach((attendee) => {
					const { pet = [], handlers = [], transferred_session = false } = attendee
					if (pet.length > 0 && handlers.length > 0 && !transferred_session) {
						attendeesMap.set(pet[0]._id, {
							handlers: handlers,
							primary_handler: handlers.find((h) => h.primary),
							handler_names: handlers.map((h) => h.full_name),
							handler_ids: handlers.map((h) => h._id),
							pet_id: pet[0]._id,
							pet_name: pet[0].name
						})
					}
					if (transferred_session) {
						foundClass.has_transfers = true
					}
				})
			})

			const details = {
				_id: foundClass._id,
				access: foundClass.access,
				instructor_id: foundClass.instructor_id?._id,
				training_location_id: foundClass.training_location_id?._id,
				training_course_id: foundClass.training_course_id?._id,
				training_course: foundClass.training_course_id,
				price_per_session: foundClass.training_course_id?.price_per_session,
				start_time: foundClass.start_time,
				end_time: foundClass.end_time,
				variable_times: foundClass.variable_times || false,
				multiple_instructors: foundClass.multiple_instructors || false,
				startDate: foundClass.startDate,
				dates: foundClass.dates.map((d) => standardizeStartOfDateUTC(d).format('YYYY-MM-DD')),
				class_id: foundClass.class_id,
				number_of_sessions:
					foundClass.number_of_sessions || foundClass.training_course_id?.number_of_sessions,
				capacity_max: foundClass.capacity_max,
				capacity_max_override: foundClass.capacity_max !== foundClass.training_course_id?.capacity_max,
				has_existing_adjustments: foundClass.has_existing_adjustments || false,
				has_transfers: foundClass.has_transfers || false,
				attendees: attendeesMap // need to filter out any session level transfers from here.
			}
			setCurrentEditedObject(details)
		}
	}, [trainingClasses, setClassDetails])

	useEffect(() => {
		const get = async () => {
			if (!params.classId || !params.facilityId) {
				return
			}
			await getTrainingClassForFacility({
				class_id: params.classId,
				facility_id: params.facilityId
			})
		}
		get()
	}, [getTrainingClassForFacility])

	useEffect(() => {
		if (!trainingCourses || trainingCourses.length === 0) {
			getTrainingCoursesForFacility()
		}
		if (!trainingLocations || trainingLocations.length === 0) {
			getTrainingLocationsForFacility()
		}
	}, [getTrainingCoursesForFacility, getTrainingLocationsForFacility])

	const classCancelled = useMemo(() => {
		if (!classDetails) {
			return false
		}
		return classDetails.cancelled
	}, [classDetails])

	const classHeader = useMemo(() => {
		if (!classDetails) {
			return 'Class Details'
		}

		const { training_course_id } = classDetails
		const { name } = training_course_id || {}

		return name || 'Class Details'
	}, [classDetails])

	const classStatus = useMemo(() => {
		if (!classDetails) {
			return null
		}

		return getClassStatus(classDetails, facility)
	}, [classDetails, facility])

	const classLabel = useMemo(() => {
		if (!classStatus) {
			return { name: 'Unknown', type: 'red' }
		}
		if (classCancelled) {
			return { name: 'Cancelled', type: 'red' }
		}
		if (classStatus.isBetween) {
			return { name: 'In Progress', type: 'green' }
		}
		if (classStatus.isBefore) {
			return { name: 'Upcoming', type: 'purple' }
		}
		if (classStatus.isAfter) {
			return { name: 'Completed', type: 'gray' }
		}
	}, [classStatus, classCancelled])

	const disableCancelClassOption = useMemo(() => {
		const noAttendees = classDetails?.attendance_by_session?.every((session) => {
			return session.attendance.length === 0
		})
		if (classStatus?.isBetween && noAttendees) {
			return false
		}
		if (classStatus?.isBefore) {
			return false
		}
		return true
	}, [classCancelled, classStatus, classDetails])

	return (
		<>
			<TrainingClassDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				onSaved={handleEditDialogClose}
				onCancelled={handleEditDialogClose}
				title="EDIT CLASS"
				mode={DIALOG_EDIT_MODES.Edit}
				trainingClass={currentEditedObject}
			/>
			<DangerConfirmationDialog
				title="Cancel Class"
				open={confirmCancelClassDialogOpen}
				description="Are you sure you want to cancel this class. All associated enrollments will be cancelled as well."
				dangerButtonText="Cancel Class"
				onConfirmDangerousAction={handleCancelClass}
				onCancel={() => setConfirmCancelClassDialogOpen(false)}
			/>
			<DangerConfirmationDialog
				title="Remove Attendee"
				open={confirmRemoveAttendeeDialogOpen}
				description={
					<span>
						This will remove{' '}
						{attendeeToRemove?.handler_names?.length > 1 ? (
							<strong>{attendeeToRemove?.handler_names.join(', ')}</strong>
						) : (
							<strong>{attendeeToRemove?.primary_handler?.full_name} </strong>
						)}{' '}
						and <strong>{attendeeToRemove?.pet_name}</strong> from this <strong>class</strong>.{' '}
						{attendeeToRemove?.primary_handler?.first_name} will not be able to re-enroll{' '}
						{attendeeToRemove?.pet_name} unless a spot is available. Refunds are not automatically
						applied at this time and will need to be manually processed for the client, according to
						the cancellation policy.
					</span>
				}
				dangerButtonText="Confirm"
				onConfirmDangerousAction={handleRemoveAttendee}
				onCancel={handleCancelRemoveAttendeeRequest}
			/>
			<Container
				className={classes.root}
				style={{
					paddingLeft: '20px !important',
					paddingRight: '20px !important'
				}}
				maxWidth="md">
				{!loading ? (
					<div>
						<div className={classes.topContainer}>
							<div className={classes.headerContainer}>
								<Typography className={classes.header}>{classHeader}</Typography>
								<StyledChip
									label={classLabel.name}
									skiptownvariant={classLabel.type}
									style={{ textTransform: 'uppercase', fontWeight: 800 }}
								/>
							</div>
							{classDetails && !classCancelled && !classStatus?.isAfter ? (
								<StyledDropdownMenu>
									<MenuItem disabled={classStatus?.isAfter} onClick={handleEditClassButtonClick}>
										Edit Class
									</MenuItem>
									<MenuItem
										disabled={disableCancelClassOption}
										onClick={handleCancelClassButtonClick}>
										Cancel Class
									</MenuItem>
								</StyledDropdownMenu>
							) : null}
						</div>
						<TrainingClassDetailsCard trainingClass={classDetails} />
						<TrainingClassSessionListCard
							trainingClass={classDetails}
							classCancelled={classCancelled}
							classStatus={classStatus}
							date={date}
							onRemoveAttendee={(attendee) => handleRemoveAttendeeRequest(attendee)}
						/>
					</div>
				) : null}
				{loading ? <Loading /> : null}
			</Container>
		</>
	)
}

export default TrainingClass
